import FeaturedWork from "@/components/portfolio/FeaturedWork";
import CmsButton from "@/components/CmsButton";
import { ModularPage, Module } from "@/components/ModularPage";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import HeroV2 from "@/components/HeroV2";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Cinema8Embed from "@/components/Cinema8Embed";
import QuickConsultationForm from "@/components/QuickConsultationForm";
import PricingConsultationForm from "@/components/PricingConsultationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Shield, Cloud, Headphones, Cpu, Check, Users, CalendarCheck } from "lucide-react";
import { useContentMulti } from "@/hooks/useContent";
import { useMemo } from "react";
import HowItWorks from "@/components/HowItWorks";
import HomeSEOBlock from "@/components/HomeSEOBlock";
import TestimonialsSection from "@/components/TestimonialsSection";
import TrustedPartners from "@/components/TrustedPartners";
import { useFaqItems } from "@/hooks/useFaqItems";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const IndexV2 = () => {
  const navigate = useNavigate();
  const [quickFormOpen, setQuickFormOpen] = useState(false);
  const [pricingFormOpen, setPricingFormOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const openQuickForm = () => setQuickFormOpen(true);
  const openPricingForm = (plan: string) => {
    setSelectedPlan(plan);
    setPricingFormOpen(true);
  };
  const c = useContentMulti({
    "v2.video.heading": "See How Smartly Transforms Your IT in Minutes",
    "v2.video.subtext": "Learn how we eliminate downtime, secure your business, and streamline operations with a fully managed IT environment.",
    "why.video_media_id": "6JM9MM8J",
    "v2.value.title": "Everything Your Business Needs. One Monthly Cost.",
    "v2.value.card1_title": "Cybersecurity First",
    "v2.value.card1_desc": "Enterprise-grade protection, monitoring, and threat response",
    "v2.value.card2_title": "Cloud & Modern Workplace",
    "v2.value.card2_desc": "Microsoft 365, Azure, and remote work enablement",
    "v2.value.card3_title": "Unlimited IT Support",
    "v2.value.card3_desc": "Fast, responsive support for your entire team",
    "v2.value.card4_title": "AI & Automation",
    "v2.value.card4_desc": "Streamline operations and increase productivity with AI",
    "v2.pricing.title": "Simple, Transparent Pricing",
    "v2.pricing.tier1_name": "Essential",
    "v2.pricing.tier1_price": "$125/user/month",
    "v2.pricing.tier1_features": "Helpdesk support,Device management,Microsoft 365 support,Patch management",
    "v2.pricing.tier2_name": "Professional",
    "v2.pricing.tier2_price": "$150/user/month",
    "v2.pricing.tier2_features": "Everything in Essential,Advanced cybersecurity,Backup & disaster recovery,Email protection",
    "v2.pricing.tier3_name": "Premium",
    "v2.pricing.tier3_price": "$175/user/month",
    "v2.pricing.tier3_features": "Everything in Professional,Compliance support,AI automation solutions,vCIO services",
    "v2.pricing.cta": "Get a Custom Quote",
    "v2.pricing.cta_link": "",
    "v2.authority.title": "Built on Enterprise IT Leadership",
    "v2.authority.content": "30+ years of IT experience. Enterprise-level IT leadership. Expertise in cybersecurity, cloud, and AI transformation.",
    "v2.target.title": "Who We Work Best With",
    "v2.target.bullets": "Growing businesses (25–100 employees),Organizations needing secure remote work,Companies looking to scale with AI and automation",
    "v2.lead.title": "Get Your Free IT & Security Assessment",
    "v2.lead.bullets": "Identify security risks,Discover cost savings,Improve performance and uptime",
    "v2.lead.cta": "Book Your Free Assessment",
    "v2.lead.cta_link": "",
    "v2.cta.title": "Ready to Simplify and Secure Your IT?",
    "v2.cta.button": "Schedule a Consultation",
    "v2.cta.button_link": "/consultation",
    "v2.faq.subtitle": "Common questions about our managed IT and cybersecurity services.",
  });

  const { data: faqItems = [] } = useFaqItems("homepage-v2");

  const jsonLd = useMemo(() => [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Smartly Technologies",
      url: "https://smartly.net",
      logo: "https://storage.googleapis.com/gpt-engineer-file-uploads/XcQUTjgyh5YSAs95nBtD4k2Lyq13/uploads/1770719365363-Smartly-Logo-Favicon-BLKBG-v01.png",
      telephone: "+18583335005",
      description: "Smartly Technologies provides managed IT support, cyber security, web & mobile development, cloud solutions, and AI integration for modern businesses in Southern California.",
    },
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Smartly Technologies",
      url: "https://smartly.net",
      telephone: "+18583335005",
      email: "support@smartly.net",
      areaServed: "Southern California",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://smartly.net/" },
      ],
    },
  ], []);

  const valueIcons = [Shield, Cloud, Headphones, Cpu];
  const valueCards = [1, 2, 3, 4].map((i) => ({
    title: c[`v2.value.card${i}_title`],
    desc: c[`v2.value.card${i}_desc`],
    Icon: valueIcons[i - 1],
  }));

  const tiers = [1, 2, 3].map((i) => ({
    name: c[`v2.pricing.tier${i}_name`],
    price: c[`v2.pricing.tier${i}_price`],
    features: c[`v2.pricing.tier${i}_features`].split(","),
    popular: i === 2,
  }));

  const targetBullets = c["v2.target.bullets"].split(",");
  const leadBullets = c["v2.lead.bullets"].split(",");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        pageSlug=""
        title="Managed IT & Cybersecurity | Smartly Technologies"
        description="Managed IT, cybersecurity, and AI solutions for growing businesses. Flat-rate pricing. Serving Southern California. Get a free IT assessment today."
        canonicalPath="/"
        jsonLd={jsonLd}
      />
      <Navbar />
      <ModularPage slug="home">
      <Module id="hero">
      <HeroV2 onConsultationClick={openQuickForm} />

      </Module>
      <Module id="video">
      {/* Section 2: Video */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{c["v2.video.heading"]}</h2>
          <Cinema8Embed mediaId={c["why.video_media_id"]} className="mb-6 rounded-xl overflow-hidden" />
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">{c["v2.video.subtext"]}</p>
          <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white border-green-600" onClick={openQuickForm}>
              <CalendarCheck className="mr-2 h-5 w-5" />
              Book Your Free IT &amp; Cybersecurity Assessment
          </Button>
        </div>
      </section>

      </Module>
      <Module id="value">
      {/* Section 3: Value Proposition */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-12">{c["v2.value.title"]}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {valueCards.map(({ title, desc, Icon }) => (
              <Card key={title} className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      </Module>
      <Module id="pricing">
      {/* Section 4: Pricing */}
      <section id="pricing" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-12">{c["v2.pricing.title"]}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {tiers.map((tier) => (
              <Card key={tier.name} className={`bg-card border-border relative ${tier.popular ? "border-primary ring-2 ring-primary/20" : ""}`}>
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{tier.name}</CardTitle>
                  <p className="text-2xl font-bold text-primary mt-2">{tier.price}</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {tier.features.map((feat) => (
                    <div key={feat} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-sm text-muted-foreground">{feat.trim()}</span>
                    </div>
                  ))}
                  <Button className={`w-full mt-4 ${tier.popular ? "glow-red" : ""}`} onClick={() => openPricingForm(tier.name)}>
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <CmsButton size="lg" variant="outline" href={c["v2.pricing.cta_link"]} onFallback={() => openPricingForm("Custom Quote")}>
              {c["v2.pricing.cta"]} <ArrowRight className="ml-2 h-4 w-4" />
            </CmsButton>
          </div>
        </div>
      </section>

      </Module>
      <Module id="how-it-works">
      {/* How It Works */}
      <HowItWorks />

      </Module>
      <Module id="authority">
      {/* Section 5: Authority */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">{c["v2.authority.title"]}</h2>
          <p className="text-muted-foreground text-lg leading-relaxed">{c["v2.authority.content"]}</p>
        </div>
      </section>

      </Module>
      <Module id="seo-block">
      {/* SEO Block - Managed IT in City */}
      <HomeSEOBlock />

      </Module>
      <Module id="trusted-partners">
      {/* Trusted Partners */}
      <TrustedPartners />

      </Module>
      <Module id="target-clients">
      {/* Section 6: Target Clients */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-10">{c["v2.target.title"]}</h2>
          <div className="space-y-4 max-w-xl mx-auto">
            {targetBullets.map((bullet) => (
              <div key={bullet} className="flex items-start gap-3">
                <Users className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span className="text-muted-foreground">{bullet.trim()}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      </Module>
      <Module id="featured-work">
      <FeaturedWork />
      </Module>
      <Module id="testimonials">
      {/* Testimonials */}
      <TestimonialsSection />

      </Module>
      <Module id="lead-capture">
      {/* Section 7: Lead Capture */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="bg-card border-border p-8 text-center">
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-6">{c["v2.lead.title"]}</h2>
            <div className="space-y-3 text-left max-w-md mx-auto mb-8">
              {leadBullets.map((bullet) => (
                <div key={bullet} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span className="text-muted-foreground">{bullet.trim()}</span>
                </div>
              ))}
            </div>
            <CmsButton size="lg" className="glow-red" href={c["v2.lead.cta_link"]} onFallback={openQuickForm}>
              {c["v2.lead.cta"]} <ArrowRight className="ml-2 h-4 w-4" />
            </CmsButton>
          </Card>
        </div>
      </section>

      </Module>
      <Module id="faq">
      {/* FAQ Section */}
      {faqItems.length > 0 && (
        <section id="faq" className="relative py-20 px-4 bg-gray-900 text-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, hsl(var(--primary) / 0.08) 0%, transparent 60%)' }} />
          <div className="container mx-auto max-w-3xl relative z-10">
            <div className="w-12 h-1 bg-primary mx-auto mb-6 rounded-full" />
            <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-3">
              Frequently Asked <span className="text-primary">Questions</span>
            </h2>
            <p className="text-gray-400 text-center mb-10">{c["v2.faq.subtitle"]}</p>
            <Accordion type="single" collapsible className="w-full flex flex-col gap-4">
              {faqItems.map((item, i) => (
                <div key={item.id} className="rounded-xl bg-white/5 border border-white/10 hover:border-primary/40 transition-all duration-300 hover:shadow-[0_0_20px_hsl(var(--primary)/0.1)] px-5 py-1">
                  <AccordionItem value={`faq-${i}`} className="border-b-0">
                    <AccordionTrigger className="text-white font-semibold text-base md:text-lg hover:no-underline gap-3">
                      <span className="flex items-center gap-4">
                        <span className="text-primary font-display text-sm shrink-0">{String(i + 1).padStart(2, '0')}</span>
                        {item.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-300 pl-10">{item.answer}</AccordionContent>
                  </AccordionItem>
                </div>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      </Module>
      <Module id="final-cta">
      {/* Section 8: Final CTA */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-8">{c["v2.cta.title"]}</h2>
          <CmsButton size="lg" className="glow-red" href={c["v2.cta.button_link"]}>
            {c["v2.cta.button"]} <ArrowRight className="ml-2 h-4 w-4" />
          </CmsButton>
        </div>
      </section>

      </Module>
      </ModularPage>
      {/* Quick 2-step form for assessment buttons */}
      <Dialog open={quickFormOpen} onOpenChange={setQuickFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-0 bg-transparent shadow-none p-0 [&>button:last-child]:hidden">
          <VisuallyHidden><DialogTitle>Book a Consultation</DialogTitle></VisuallyHidden>
          <QuickConsultationForm onClose={() => setQuickFormOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Pricing 3-step form */}
      <Dialog open={pricingFormOpen} onOpenChange={setPricingFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-0 bg-transparent shadow-none p-0 [&>button:last-child]:hidden">
          <VisuallyHidden><DialogTitle>Get Started with Smartly</DialogTitle></VisuallyHidden>
          <PricingConsultationForm preselectedPlan={selectedPlan} plans={tiers.map(t => ({ name: t.name, price: t.price }))} onClose={() => setPricingFormOpen(false)} />
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default IndexV2;
